import { z } from 'zod';

/**
 * Variant and control statistics of a campaign or Canvas (BB-136, AN-004/AN-006): each variant's conversion rate with
 * a 95 % Wilson interval, its uplift over control, a two-proportion z-test with Holm correction over the comparisons,
 * and the minimum sample per group for the chosen minimum detectable effect. A winner is named only when the groups
 * reached that sample and the difference holds after correction; without a control group there is no uplift and no
 * winner (DEC-017: no causal uplift without control).
 *
 * `basis=assigned` (default, intent-to-treat): every profile assigned to a group counts, whether or not the message
 * reached it — campaign assignments (`variant_assignment`, BB-70), Canvas entries. `basis=exposed`: only profiles whose
 * conversion window opened (BB-103: receipt for variants, assignment for control), which is not symmetric.
 */
export const VARIANT_STATS_BASES = ['assigned', 'exposed'] as const;
export type VariantStatsBasis = (typeof VARIANT_STATS_BASES)[number];

/** Defaults of the method; the minimum detectable effect is relative (0.1 = a 10 % change of the control rate). */
export const VARIANT_STATS_ALPHA = 0.05;
export const VARIANT_STATS_POWER = 0.8;
export const VARIANT_STATS_DEFAULT_MDE = 0.1;

const Day = z.iso.date();

export const VariantStatsQuery = z
  .object({
    /** A conversion of the published revision / version; the primary (first) one by default. */
    conversionId: z.uuid().optional(),
    basis: z.enum(VARIANT_STATS_BASES).default('assigned'),
    /** Relative minimum detectable effect for the sample size, 1 %…500 %. */
    mde: z.coerce.number().min(0.01).max(5).default(VARIANT_STATS_DEFAULT_MDE),
    /** Optional period (environment days) on the assignment / window start; whole history without it. */
    from: Day.optional(),
    to: Day.optional(),
  })
  .refine((q) => (q.from === undefined) === (q.to === undefined), { message: 'from and to go together', path: ['from'] })
  .refine((q) => !q.from || !q.to || q.from <= q.to, { message: 'from must not be after to', path: ['from'] })
  .refine((q) => !q.from || !q.to || (Date.parse(q.to) - Date.parse(q.from)) / 86_400_000 <= 366, { message: 'at most 366 days', path: ['to'] });
export type VariantStatsQuery = z.infer<typeof VariantStatsQuery>;

export const VARIANT_STATS_WARNINGS = [
  /** No control group: rates and intervals only, no uplift, no p-values, no winner. */
  'no_control',
  /** Some group is below the minimum sample: no winner yet. */
  'insufficient_sample',
  /** Control has no conversions (or all converted): the minimum sample cannot be estimated. */
  'baseline_unknown',
  /** `basis=exposed`: control counts from assignment, variants from receipt — the groups are not alike. */
  'exposure_asymmetric',
  /** `basis=assigned`: some variant profiles never received the message and count as not converted. */
  'unexposed_assignments',
  /** Variant sends were aborted (Liquid abort, missing data); control renders nothing, so it keeps such profiles. */
  'aborts_skew_control',
  /** There are assignments but no conversion window opened yet (e.g. Canvas before windows from entry). */
  'no_conversion_windows',
] as const;
export type VariantStatsWarningCode = (typeof VARIANT_STATS_WARNINGS)[number];

export interface VariantStatsWarning {
  code: VariantStatsWarningCode;
  uk: string;
  en: string;
  /** How many profiles the warning is about, where it counts something. */
  count?: number;
}

export interface VariantStatsRow {
  variantApiId: string;
  /** From the published revision / version; null for a variant only seen in data (an older revision). */
  name: string | null;
  isControl: boolean;
  /** Distinct profiles assigned to the group (Canvas: entered with this variant). */
  assigned: number;
  /** Distinct profiles whose conversion window opened. */
  exposed: number;
  /** Distinct converted profiles among the basis denominator. */
  conversions: number;
  /** `conversions / (assigned | exposed)`; null with an empty denominator. */
  rate: number | null;
  /** 95 % Wilson interval of `rate`. */
  ci95: [number, number] | null;
  /** Variants: `rate − control rate` (absolute). */
  differenceVsControl: number | null;
  /** Variants: `(rate − control rate) / control rate`; null when control converts at 0 %. */
  upliftVsControl: number | null;
  /** Variants: two-sided p-value of the z-test against control, before correction. */
  pValue: number | null;
  /** Variants: Holm-adjusted p-value over all comparisons with control. */
  pValueAdjusted: number | null;
  /** Variants: Φ(z), Braze's «confidence» that the variant converts better than control (one-sided, uncorrected). */
  confidence: number | null;
  /** Variants: adjusted p-value below alpha. */
  significant: boolean;
  /** The basis denominator against `requiredPerVariant`. */
  sampleStatus: 'insufficient' | 'sufficient';
  /** Distinct profiles with an aborted send (`aborted`, `iam_aborted`); null where it cannot be attributed (Canvas). */
  aborted: number | null;
  /** Control row: variants had aborted sends, so control keeps profiles a variant would have dropped. */
  skewedByAborts: boolean;
}

export type VariantStatsVerdict = 'winner' | 'no_difference' | 'insufficient_sample' | 'no_control' | 'no_data';

export interface VariantStatsConversion {
  id: string;
  kind: 'custom_event' | 'session_start';
  event?: string;
  windowSeconds: number;
  primary: boolean;
}

export interface VariantStatsView {
  object: { type: 'campaign' | 'canvas'; id: string };
  basis: VariantStatsBasis;
  from: string | null;
  to: string | null;
  /** The conversion measured, and the ones that can be picked instead. */
  conversion: VariantStatsConversion;
  conversions: VariantStatsConversion[];
  method: { test: 'two_proportion_z'; correction: 'holm'; interval: 'wilson'; alpha: number; power: number; mde: number; comparisons: number };
  /** Control rate the sample size is planned on. */
  baselineRate: number | null;
  /** Per group, for `mde` at `alpha / comparisons` (Holm's strictest step) and `power`; null when it cannot be estimated. */
  requiredPerVariant: number | null;
  rows: VariantStatsRow[];
  /** A variant (or `control`, when every variant is significantly worse) — only with a sufficient sample. */
  winner: string | null;
  verdict: VariantStatsVerdict;
  warnings: VariantStatsWarning[];
  /** The method in words, for the explanation next to the table. */
  explanation: { uk: string; en: string };
}
